import { forwardRef, type ReactNode, type SelectHTMLAttributes } from 'react';
import { clsx } from 'clsx';
import { ChevronDown } from 'lucide-react';
import { Field, controlClass } from './Field';

export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
  disabled?: boolean;
  /** Optional right-aligned count, e.g. how many vehicles match. */
  count?: number;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  hideLabel?: boolean;
  size?: 'sm' | 'md';
  options: SelectOption[];
  /** Shown as the first, empty option — the "no filter" state. */
  placeholder?: string;
  containerClassName?: string;
}

/**
 * A single-choice control built on the native `<select>`.
 *
 * Native on purpose: it behaves correctly on a phone, inherits the platform's own
 * keyboard handling, and there is nothing about picking one model from a list that a
 * custom listbox would do better. `MultiSelect` exists for the cases that genuinely
 * need more.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    hint,
    error,
    hideLabel,
    size = 'md',
    options,
    placeholder,
    required,
    className,
    containerClassName,
    disabled,
    ...rest
  },
  ref,
) {
  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={required}
      hideLabel={hideLabel}
      className={containerClassName}
    >
      {({ id, describedBy, invalid }) => (
        <div className="relative flex min-w-0 items-center">
          <select
            ref={ref}
            id={id}
            required={required}
            disabled={disabled}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            className={clsx(
              controlClass({ size, invalid, disabled }),
              'appearance-none pr-8',
              className,
            )}
            {...rest}
          >
            {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.count === undefined ? option.label : `${option.label} (${option.count})`}
              </option>
            ))}
          </select>
          <ChevronDown
            aria-hidden
            className="pointer-events-none absolute right-2.5 h-4 w-4 text-faint"
          />
        </div>
      )}
    </Field>
  );
});
